import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DatePicker } from 'primeng/datepicker';
import { Calendar, CalendarModule } from 'primeng/calendar';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { ShadowDomStyleInjectorService } from '@everymatrix-base-components/utils';

@Component({
  selector: 'lib-dm-date-picker-lib',
  templateUrl: './dm-date-picker-lib.component.html',
  styleUrl: './dm-date-picker-lib.component.scss',
  standalone: true,
  imports: [DatePicker, CalendarModule, FormsModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DmDatePickerLibComponent),
      multi: true,
    },
  ],
})
export class DmDatePickerLibComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  @ViewChild('calendar') calendar!: Calendar;

  /**
   * Label text displayed above the input
   */
  @Input() label = 'Date Picker';

  /**
   * Placeholder text inside the input
   */
  @Input() placeholder = 'MM.DD.YYYY';

  /**
   * Disables the input field if set to true
   */
  @Input({ transform: booleanAttribute }) disabled = false;

  /**
   * Marks the input as required if set to true
   */
  @Input({ transform: booleanAttribute }) isRequired = false;

  /**
   * Determines whether the input should have a default date set.
   * If true, a predefined date will be applied.
   */
  @Input({ transform: booleanAttribute }) hasDefaultDate = true;

  /**
   * Initial value provided by the parent component.
   * Usage: For single date select pass a string representation of a date (e.g. "2025-01-01").
   */
  private _initialValue: Date = new Date();
  @Input()
  set initialValue(value: string | Date) {
    if (value) {
      this._initialValue = typeof value === 'string' ? new Date(value) : value;
      this.dateControl.setValue(this._initialValue);
    }
  }
  get initialValue(): Date {
    return this._initialValue;
  }

  /**
   * The minimum selectable date.
   * Usage: Pass a date string (e.g. "2025-01-01") or a Date object.
   */
  private _minDate = new Date();
  @Input()
  set minDate(value: string | Date) {
    if (value) {
      this._minDate = typeof value === 'string' ? new Date(value) : value;
    }
  }
  get minDate(): Date {
    return this._minDate;
  }

  /**
   * The maximum selectable date.
   * Usage: Pass a date string (e.g. "2025-12-31") or a Date object.
   */
  private _maxDate!: Date;
  @Input()
  set maxDate(value: string | Date) {
    if (value) {
      this._maxDate = typeof value === 'string' ? new Date(value) : value;
    }
  }
  get maxDate(): Date {
    return this._maxDate;
  }

  /**
   * Array with dates that should be disabled (not selectable).
   * Usage: Pass a date string (e.g. "2025-05-01") or a Date object.
   * If no date should be disabled, pass null.
   */
  private _disabledDates: Date | null = null;
  @Input()
  set disabledDates(value: string | Date | null) {
    if (value) {
      this._disabledDates = typeof value === 'string' ? new Date(value) : value;
    }
  }
  get disabledDates(): Date | null {
    return this._disabledDates;
  }

  /**
   * Array with weekday numbers that should be disabled (not selectable).
   * Usage: Pass a JSON stringified array of numbers (e.g. "[0,6]") or an array of numbers.
   */
  private _disabledDays: number[] | null = null;
  @Input()
  set disabledDays(value: string | number[] | null) {
    if (typeof value === 'string') {
      try {
        this._disabledDays = JSON.parse(value);
      } catch (e) {
        this._disabledDays = null;
      }
    } else {
      this._disabledDays = value;
    }
  }
  get disabledDays(): number[] | null {
    return this._disabledDays;
  }

  /**
   * Format of the date.
   */
  @Input() dateFormat = 'dd/mm/yy';

  /**
   * Shows time picker.
   */
  @Input({ transform: booleanAttribute }) showTimePicker = false;

  dateControl: FormControl<Date | null> = new FormControl<Date | null>(null);

  constructor(
    public elementRef: ElementRef,
    private shadowDomStyleInjectorService: ShadowDomStyleInjectorService
  ) {
    this.dateControl.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.elementRef.nativeElement.dispatchEvent(
        new CustomEvent('dm-date-picker-changed', {
          detail: value,
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  onChange: (value: Date | null) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    if (this.hasDefaultDate && this.initialValue) {
      this.dateControl.setValue(this.initialValue);
    }
  }

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.elementRef)
      .subscribe();
  }

  onCalendarShow(): void {
    setTimeout(() => {
      this.patchCalendarClickHandler();
    });
  }

  /**
   * Workaround for a known PrimeNG calendar bug when used inside Shadow DOM.
   *
   * Issue:
   * PrimeNG Calendar (v19.x) does not handle click events properly within Shadow DOM,
   * causing the overlay panel to close immediately when clicking inside the calendar popup.
   *
   * Reason:
   * The internal documentClickListener does not support composedPath() and does not account for Shadow DOM encapsulation,
   * resulting in incorrect click detection.
   *
   * Fix:
   * We patch the click listener to use `composedPath()` and `instanceof Node` to safely detect if the click occurred inside the calendar
   * or its overlay, even when rendered in Shadow DOM.
   *
   */
  private patchCalendarClickHandler(): void {
    const calendarRef = this.calendar;

    if (!calendarRef || !calendarRef.overlay) return;

    if (calendarRef.documentClickListener) {
      calendarRef.documentClickListener();
    }

    calendarRef.documentClickListener = calendarRef.renderer.listen(
      'document',
      'click',
      (event: Event) => {
        const path = (event as any).composedPath?.() || [];

        const clickedInside = path.some(
          (el: any) =>
            el instanceof Node &&
            (calendarRef.el.nativeElement.contains(el) ||
              calendarRef.overlay?.contains(el))
        );

        if (!clickedInside) {
          calendarRef.hideOverlay?.();
        }
      }
    );
  }

  writeValue(value: Date | null): void {
    this.dateControl.setValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    if (this.disabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }
}
